import React from "react";
import mapHelper from "@mrwa/rime-replacement/infrastructure/mapHelper";
import requestEsriModules from "@mrwa/rime-replacement/infrastructure/esriModules";
import irisRoadData from "../data/irisRoadData";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMapPin } from "@fortawesome/free-solid-svg-icons";
import { ReactComponent as PinLeft } from "./pin-left.svg";
import { ReactComponent as PinRight } from "./pin-right.svg";
import coordinateListState from "./coordinateListState";
import {
  ApplicationMessage,
  messageState,
} from "@mrwa/rime-replacement/esriControls/general/ApplicationMessage";

import "./addCoordinateToolBar.css";

export const geometryType = {
  POLYLINE: "polyline",
  POINT: "point",
};

export default class AddCoordinateToolBar extends React.Component {
  constructor(props) {
    super(props);
    this.state = { isRecording: false };

    this.startCoordinates = null;
    this.irisDataSet = false;

    this.handleAddPoint = this.handleAddPoint.bind(this);
    this.startCapture = this.handleCapture.bind(this, true);
    this.endCapture = this.handleCapture.bind(this, false);
    this.gpsFileChanged = this.gpsFileChanged.bind(this);
  }

  componentDidUpdate() {
    if (this.irisDataSet) return;
    if (this.props.playerContext && this.props.irisRoadLayerConfig) {
      irisRoadData.initialise(
        this.props.irisRoadLayerConfig,
        this.props.playerContext
      );
      this.props.playerContext.onGpsFileChanged(this.gpsFileChanged.bind(this));
      this.irisDataSet = true;
    }
  }

  gpsFileChanged() {
    this.startCoordinates = null;
    this.setState({ isRecording: false });
  }

  //returns a point geometry from a given latitude and longitude
  async toPointGeometry(pointGeometry) {
    const { latitude, longitude } = pointGeometry;
    if (isNaN(latitude) || isNaN(longitude)) return;
    const { Point } = await requestEsriModules({
      Point: "esri/geometry/Point",
    });
    const geometry = new Point({
      latitude,
      longitude,
      spatialReference: { wkid: 4326 }, //4326 is WGS84, spatial reference used by GPS systems
    });
    return geometry;
  }

  //returns a polyline geometry from a given latitude and longitude
  async toPolylineGeometry(lineGeometry) {
    if (
      isNaN(lineGeometry.startLatitude) ||
      isNaN(lineGeometry.startLongitude) ||
      !lineGeometry.endIndex ||
      lineGeometry.startIndex === null //have to compare to null due a 0 index being false and possible.
    )
      return;
    const recordedPath = [];
    for (let i = lineGeometry.startIndex; i < lineGeometry.endIndex; i++) {
      const point = [
        this.props.playerContext.gpsInformation.allData[i].longitude,
        this.props.playerContext.gpsInformation.allData[i].latitude,
      ];
      recordedPath.push(point);
    }
    const { Polyline } = await requestEsriModules({
      Polyline: "esri/geometry/Polyline",
    });
    const path = [recordedPath];
    const geometry = new Polyline({
      paths: path,
      spatialReference: { wkid: 4326 }, //4326 is WGS84, spatial reference used by GPS systems
    });
    return geometry;
  }

  async handleAddPoint() {
    if (
      irisRoadData.currentRoadData &&
      ((irisRoadData.currentRoadData.latitude &&
        irisRoadData.currentRoadData.longitude) ||
        irisRoadData.currentRoadData.slk)
    ) {
      const pointCoordinate = {
        roadNo: irisRoadData.currentRoadData.roadNo || "",
        roadName: irisRoadData.currentRoadData.roadName || "",
        slk: irisRoadData.currentRoadData.slk
          ? irisRoadData.currentRoadData.slk.toFixed(2)
          : "",
        latitude:
          irisRoadData.playerContext.gpsInformation.current.latitude || "",
        longitude:
          irisRoadData.playerContext.gpsInformation.current.longitude || "",
        note: "",
        type: geometryType.POINT,
      };
      const pointCoord = {
        ...pointCoordinate,
        geometry: await this.toPointGeometry(pointCoordinate),
      };
      coordinateListState.savedCoordinates = [
        ...coordinateListState.savedCoordinates,
        pointCoord,
      ];
    } else {
      messageState.message = ApplicationMessage.Error(
        "No data is available for the current position"
      );
    }
  }

  async calculateStraightLineDistance(coordinate) {
    if (
      !coordinate.startLatitude ||
      !coordinate.startLongitude ||
      !coordinate.endLatitude ||
      !coordinate.endLongitude
    )
      return;
    const { GeometryEngine } = await requestEsriModules({
      GeometryEngine: "esri/geometry/geometryEngineAsync",
    });
    const startData = {
      latitude: coordinate.startLatitude,
      longitude: coordinate.startLongitude,
    };
    const endData = {
      latitude: coordinate.endLatitude,
      longitude: coordinate.endLongitude,
    };

    const convertedStartPoint = await mapHelper.projectToView(
      await this.toPointGeometry(startData)
    );
    const convertedEndPoint = await mapHelper.projectToView(
      await this.toPointGeometry(endData)
    );

    return await GeometryEngine.distance(
      convertedStartPoint,
      convertedEndPoint,
      "meters"
    );
  }

  async calculateDistance(geometry) {
    const { GeometryEngine } = await requestEsriModules({
      GeometryEngine: "esri/geometry/geometryEngineAsync",
    });

    return await GeometryEngine.geodesicLength(geometry, "meters");
  }

  async handleCapture(isRecording) {
    if (
      irisRoadData.currentRoadData &&
      ((irisRoadData.currentRoadData.latitude &&
        irisRoadData.currentRoadData.longitude) ||
        irisRoadData.currentRoadData.slk)
    ) {
      let endCoordinate = null;
      const gpsInformation = this.props.playerContext.gpsInformation;
      this.setState({ isRecording: isRecording });
      if (isRecording) {
        const startIndex = gpsInformation.allData.findIndex(
          (x) => x.sortingKey === gpsInformation.current.sortingKey
        );
        this.startCoordinates = {
          startIndex: startIndex >= 0 ? startIndex : null,
          startRoadNo: irisRoadData.currentRoadData.roadNo || "",
          startRoadName: irisRoadData.currentRoadData.roadName || "",
          startSLK: irisRoadData.currentRoadData.slk
            ? irisRoadData.currentRoadData.slk.toFixed(2)
            : "",
          startLatitude: gpsInformation.current.latitude || "",
          startLongitude: gpsInformation.current.longitude || "",
        };
      } else {
        endCoordinate = {
          endIndex:
            gpsInformation.allData.findIndex(
              (x) => x.sortingKey === gpsInformation.current.sortingKey
            ) || null,
          endRoadNo: irisRoadData.currentRoadData.roadNo || "",
          endRoadName: irisRoadData.currentRoadData.roadName || "",
          endSLK: irisRoadData.currentRoadData.slk
            ? irisRoadData.currentRoadData.slk.toFixed(2)
            : "",
          endLatitude: gpsInformation.current.latitude || "",
          endLongitude: gpsInformation.current.longitude || "",
        };
      }
      const combinedCoordinate = {
        ...this.startCoordinates,
        ...endCoordinate,
        note: "",
        type: geometryType.POLYLINE,
      };
      if (endCoordinate) {
        const geometry = await this.toPolylineGeometry(combinedCoordinate);
        const resultCoordinate = {
          ...combinedCoordinate,
          geometry,
          straightLineDistance: await this.calculateStraightLineDistance(
            combinedCoordinate
          ),
          alongRoadDistance: await this.calculateDistance(geometry),
        };

        coordinateListState.savedCoordinates = [
          ...coordinateListState.savedCoordinates,
          resultCoordinate,
        ];
        //reset start and end coordinates
        this.startCoordinates = null;
        endCoordinate = null;
      } else return;
    } else {
      messageState.message = ApplicationMessage.Error(
        "No data is available for the current position"
      );
    }
  }

  render() {
    if (!this.props.playerContext || !this.props.playerContext.hasData)
      return null;

    //use esri styles to look the same as the map buttons
    const buttonClass = "esri-widget--button";
    const buttonClassDisabled = "esri-widget--button esri-button--disabled";
    return (
      <div className="coordinateTools">
        <div
          disabled={this.state.isRecording}
          onClick={this.handleAddPoint}
          className={this.state.isRecording ? buttonClassDisabled : buttonClass}
          type="button"
          title="Add Point"
        >
          <FontAwesomeIcon icon={faMapPin}></FontAwesomeIcon>
        </div>
        <div
          disabled={this.state.isRecording}
          className={this.state.isRecording ? buttonClassDisabled : buttonClass}
          type="button"
          title="Start Capture"
          onClick={this.startCapture}
        >
          <PinRight className="customIcon" />
        </div>
        <div
          className={
            !this.state.isRecording ? buttonClassDisabled : buttonClass
          }
          type="button"
          title="End Capture"
          disabled={!this.state.isRecording}
          onClick={this.endCapture}
        >
          <PinLeft className="customIcon" />
        </div>
      </div>
    );
  }
}
